import './GameCard.scss';
import { type ReactEventHandler, useCallback, useEffect, useRef, useState } from 'react';

import classNames from 'classnames';
import isEmpty from 'lodash/isEmpty';
import { observer } from 'mobx-react';

import {
  GAME_LAUNCH_CATEGORY,
  GAME_LAUNCH_HORIZONTAL_LOCATION,
  GAME_LAUNCH_VERTICAL_LOCATION,
} from '../../config/constants';
import { usePhoneVerifyDialog } from '../../hooks/usePhoneVerifyDialog';
import { useStartGame } from '../../hooks/useStartGame';
import { useVisibility } from '../../hooks/useVisibility';
import { useFlag } from '../../services/launchDarkly';
import type { Game } from '../../stores/game/types';
import { useStores } from '../../stores/useStores';
import { getThumbnailUrl } from '../../utils/getThumbnailUrl';
import { JackpotBalance } from '../JackpotBalance/JackpotBalance';
import { PreloadBlur } from '../PreloadBlur/PreloadBlur';

import { GAME_CARD_ID_PREFIX } from './constants';
import { CrownJackpotBadge } from './CrownJackpotBadge/CrownJackpotBadge';
import { jackpotCoinsAttr } from './CrownJackpotBadge/jackpotCoinsAttr';
import { GameCardV2 } from './GameCardV2/GameCardV2';
import { getGamePosition } from './getGamePosition';
import { useGameLabels } from './hooks/useGameLabels';
import { isJackpotGame } from './isJackpotGame';
import { PlayingNowSlot } from './PlayingNowBadge/PlayingNowSlot';
import { TestingBadge } from './TestingBadge/TestingBadge';
interface GameCardProps {
  game: Game;
  rowName: string;
  gameLabelFF: boolean;
  onboarding?: boolean;
  tooltipRef?: null | ((el: HTMLElement | null) => void);
  carousel?: boolean;
  animated?: boolean;
  /**
   * This card repeats one that is already in the document — a carousel's marquee copies (PLT-795).
   * A repeat must not claim the card's document-unique `id`: four call sites resolve a game to its
   * card with `document.querySelector('#game-card-<id>')` and would otherwise find whichever copy
   * comes first in the DOM rather than the one the player is looking at.
   */
  duplicate?: boolean;
  /*
    Some game in this card's row carries the jackpot bar. Drops the icon labels to the same height
    on every tile in the row, not only on the jackpot ones. Read by the old card only — `GameCardV2`
    places its icon labels against the artwork, which is already level across a row.
  */
  rowHasJackpot?: boolean;
}
export const GameCardOld = observer(
  ({
    game,
    rowName,
    gameLabelFF,
    onboarding,
    tooltipRef,
    carousel,
    animated = true,
    duplicate,
    rowHasJackpot = false,
  }: GameCardProps) => {
    const { userStore, coinTypeStore } = useStores();
    const { tagGameLabels, iconGameLabels } = useGameLabels(game, gameLabelFF);

    const { masterCasinoGameId, identifier, name: gameName } = game;

    const { coinType } = coinTypeStore;

    const isJackpot = isJackpotGame(game);

    const [shouldStickVertically, setShouldStickVertically] = useState(false);

    const { ref, visible } = useVisibility<HTMLAnchorElement>({
      defaultState: false,
      disable: !isJackpot,
    });

    const [isImageLoaded, setIsImageLoaded] = useState(false);

    const { handleStartGame } = useStartGame();
    const { openVerifyPhoneDialog } = usePhoneVerifyDialog();

    const isWaitingForVerification = useRef(false);
    const cardRef = useRef<HTMLAnchorElement | null>(null);

    const isPhoneVerificationRequired = useFlag('phone_verification_dialog', true);
    const phoneVerified =
      !isPhoneVerificationRequired || (userStore.profile?.phone && userStore.profile?.phoneVerified);

    const startGame = useCallback(() => {
      const position = cardRef.current ? getGamePosition(cardRef.current) : null;
      if (position) {
        localStorage.setItem(GAME_LAUNCH_VERTICAL_LOCATION, String(position.vertical_location));
        localStorage.setItem(GAME_LAUNCH_HORIZONTAL_LOCATION, String(position.horizontal_location));
      }
      localStorage.setItem(GAME_LAUNCH_CATEGORY, rowName);
      handleStartGame(masterCasinoGameId, rowName);
    }, [masterCasinoGameId, handleStartGame, rowName]);

    const attemptStartingGame = useCallback(
      (event: React.MouseEvent<HTMLAnchorElement>) => {
        event.preventDefault();

        if (!phoneVerified && coinType === 'SC') {
          let success = false;
          isWaitingForVerification.current = true;

          const cancelVerification = () => {
            isWaitingForVerification.current = success;
          };

          const onSuccess = () => {
            success = true;
          };

          openVerifyPhoneDialog(onSuccess, {
            beforeClose: cancelVerification,
          });
        } else {
          startGame();
        }
      },
      [openVerifyPhoneDialog, startGame, phoneVerified, coinType],
    );

    useEffect(() => {
      if (isWaitingForVerification.current && phoneVerified) {
        startGame();
      }
    }, [phoneVerified, startGame]);

    const handleImageLoad = useCallback<ReactEventHandler<HTMLImageElement>>((event) => {
      setIsImageLoaded(true);
      const { height, width, parentElement } = event.currentTarget;

      if (!parentElement) {
        return;
      }

      const parentRatio = parentElement.clientHeight / parentElement.clientWidth;
      const ownRatio = height / width;

      setShouldStickVertically(parentRatio > ownRatio);
    }, []);

    const mergedRef = useCallback(
      (element: HTMLAnchorElement | null) => {
        tooltipRef?.(element);
        (ref as any).current = element;
        cardRef.current = element;
      },
      [ref, tooltipRef],
    );

    const hasTagLabel = !isEmpty(tagGameLabels);
    const hasIconLabel = !isEmpty(iconGameLabels);
    return (
      <a
        ref={mergedRef}
        className={classNames('game-card', 'game-card_v1', {
          'game-card_onboarding': onboarding,
          'onboarding-arrow': onboarding,
          'game-card_in_carousel': carousel,
          'animation-popup': animated,
        })}
        data-game-id={masterCasinoGameId}
        data-game-name={name}
        /*
          The Crown Jackpot badge's CSS hook — the coins this game contributes to. The badge below is
          rendered whenever the game has any; these two attributes plus `<body>`'s decide whether it is
          drawn. See `CrownJackpotBadge.module.scss`.
        */
        data-jackpot-coins={jackpotCoinsAttr(game.jackpots)}
        data-testid={duplicate ? undefined : identifier}
        href={`/games/${masterCasinoGameId}`}
        id={duplicate ? undefined : GAME_CARD_ID_PREFIX + masterCasinoGameId}
        onClick={attemptStartingGame}
      >
        <div className={classNames('game-card_content')}>
          <PreloadBlur
            className={classNames({ ['game-card__skeleton']: !isImageLoaded })}
            render={(props) => (
              <>
                <img
                  {...props}
                  alt={gameName}
                  className={shouldStickVertically ? 'game-card__thumbnail--stick-vertically' : undefined}
                  crossOrigin="anonymous"
                  data-testid={duplicate ? undefined : identifier}
                  loading="lazy"
                  src={getThumbnailUrl(game)}
                  onLoad={(event) => {
                    handleImageLoad(event);
                    props.onLoad?.();
                  }}
                />
                {hasIconLabel && (
                  <div
                    className={classNames('game-card__icon_labels', {
                      'with-jackpot': isJackpot || rowHasJackpot,
                    })}
                  >
                    {iconGameLabels.map(({ id, iconSrc }) => (
                      <img key={id} className="game-card__icon_label" crossOrigin="anonymous" src={iconSrc} />
                    ))}
                  </div>
                )}
                {hasTagLabel && (
                  <div className="game-card__labels">
                    {tagGameLabels
                      .filter(({ name }) => name)
                      .map(({ id, name, gcStyle, scStyle }) => {
                        const style = coinType === 'GC' ? { ...gcStyle } : { ...scStyle };

                        return (
                          <span
                            key={id}
                            className={classNames('game-card__label', {
                              multiple: tagGameLabels.length > 1,
                            })}
                            style={style}
                          >
                            <span className="game-card__label_internal">{name.toUpperCase()}</span>
                          </span>
                        );
                      })}
                  </div>
                )}
                {game.isTestingOnly && <TestingBadge belowJackpot={isJackpot} />}
              </>
            )}
          />

          {isJackpot && visible && (
            <div className="jackpot_parent">
              <JackpotBalance
                badgeUrl={game.masterCasinoProvider?.jackpotBadgeUrl}
                providerId={game.masterCasinoProviderId}
                providerName={game.masterCasinoProvider?.name}
              />
            </div>
          )}

          {/*
          Crown Jackpot badge (KRA-303, frame 10083:13431). Suppressed on a tile that already carries
          the legacy Crownslots feed badge — they occupy the same corner and stacking two jackpot
          marks on one thumbnail reads as a rendering fault, not as two jackpots.

          `gameId` is the master id: core's /jackpot/games returns it alongside brandGameId, and the
          master id is the only one a lobby tile knows about.
        */}
          {game.jackpots?.length ? <CrownJackpotBadge /> : null}
        </div>
        {/* Sibling of `_content` so it escapes `overflow: hidden`. */}
        <PlayingNowSlot identifier={identifier} masterCasinoGameId={masterCasinoGameId} />
      </a>
    );
  },
);

export const GameCard = (props: GameCardProps) => {
  const isUiRedesignGameItem = useFlag('ui_redesign_game_item', false);

  if (isUiRedesignGameItem) {
    return <GameCardV2 {...props} />;
  }

  return <GameCardOld {...props} />;
};
